import React, { useEffect, useMemo, useRef } from 'react';
import { ModalBody, ModalFooter, Button, Alert, RadioGroup, Text, Box, } from '@spothero/ui';
import { MoreTime } from '@spothero/icons';
import NewTimesDisplay from '../new-times-display';
import { useSelector, useDispatch } from 'react-redux';
import UserUtils, { PAYPAL_WALLET_VALUE } from 'utils/user-utils';
import { useDefaultCard } from 'common/payment-picker/use-payment-form';
import RefundInfo from './RefundInfo';
import { refreshUser } from 'store/user/user-actions';
import { updateReservationTimes } from 'store/parking-pass/parking-pass-actions';
import BorderedRadio from 'common/bordered-radio';
import { Controller, useForm } from 'react-hook-form';
import FormatUtils from 'utils/format';
import { getCardInitials } from 'utils/payment-utils';
import { scrollToRef } from 'utils/dom-utils';
export const REFUND_FIELD = 'refund';
export const REFUND_FORM_ID = 'REFUND_FORM_ID';
export const CREDIT_VALUE = 'CREDIT_VALUE';
export const ORIGINAL_PAYMENT_METHOD_VALUE = 'ORIGINAL_PAYMENT_METHOD';
const NO_SELECTION_ERROR = 'NO_SELECTION_ERROR';
const GENERIC_ERROR = 'GENERIC_ERROR';
const ERROR_MAPPING = {
    [NO_SELECTION_ERROR]: {
        title: 'Please choose where to send your refund',
    },
    [GENERIC_ERROR]: {
        title: 'Oops, we hit a snag issuing your refund',
        description: 'Please try again.',
    },
};
const ChangeTimesRefund = ({ onCloseModal, handleBackToTimeSelect, rateDiff, timezone, isAutoExtension, reservation, setIsSubmitting, isSubmitting, onSuccess, onError, }) => {
    const { creditCard: reservationCreditCard } = reservation;
    const defaultCard = useDefaultCard();
    const dispatch = useDispatch();
    const { status: userStatus } = useSelector((state) => state.user.data);
    const errorMessageRef = useRef();
    const onlyOriginalPaymentView = userStatus !== UserUtils.AUTH_STATE.USER;
    const refundCard = reservationCreditCard
        ? reservationCreditCard
        : defaultCard;
    const { refundAmount } = rateDiff.priceBreakdown;
    const onlyCreditView = refundCard?.deleted || refundAmount.charge === 0;
    const noOptions = onlyOriginalPaymentView || onlyCreditView;
    const cardDisplay = useMemo(() => {
        if (!refundCard) {
            return null;
        }
        if (refundCard?.wallet === PAYPAL_WALLET_VALUE) {
            return `Paypal ${refundCard.paypalEmail}`;
        }
        else {
            const cardTypeFormatted = FormatUtils.slugify(refundCard.type);
            return (<>
                    {getCardInitials(cardTypeFormatted)}
                    <sup>*</sup>
                    {refundCard.last4}
                </>);
        }
    }, [refundCard]);
    const { control, handleSubmit, setError, reset, formState: { errors }, register, } = useForm();
    const error = errors[REFUND_FIELD];
    useEffect(() => {
        return reset();
    }, [reset]);
    const handleSuccess = ({ refundAsCredit }) => {
        onSuccess({
            refundAsCredit,
        });
        try {
            dispatch(refreshUser());
        }
        catch { } // eslint-disable-line no-empty
    };
    const onSubmit = async ({ refund: refundCardValue }) => {
        if (!refundCardValue) {
            setError(REFUND_FIELD, {
                type: 'custom',
                message: NO_SELECTION_ERROR,
            });
            scrollToRef(errorMessageRef);
            return;
        }
        setIsSubmitting(true);
        let cardExternalId = refundCard?.externalId || null;
        if (refundCardValue === ORIGINAL_PAYMENT_METHOD_VALUE ||
            refundCardValue === CREDIT_VALUE) {
            cardExternalId = null;
        }
        const response = await dispatch(updateReservationTimes({
            reservationId: reservation.rentalId,
            rateDiff,
            timezone,
            refundAsCredit: refundCardValue === CREDIT_VALUE,
            cardExternalId,
        }));
        // @ts-expect-error
        if (response.success) {
            handleSuccess({
                refundAsCredit: refundCardValue === CREDIT_VALUE,
            });
        }
        else {
            // @ts-expect-error
            onError(response.error);
            setError(REFUND_FIELD, {
                type: 'custom',
                message: GENERIC_ERROR,
            });
            scrollToRef(errorMessageRef);
        }
    };
    return (<>
            <ModalBody flexDirection="column" data-testid="ChangeTimesRefund">
                {isAutoExtension && (<Alert marginBottom={6} showIcon icon={MoreTime} status="neutral" description="You're getting more time than you selected at no additional cost. Happy parking!"/>)}
                <NewTimesDisplay rateDiff={rateDiff} timezone={timezone} handleBackToTimeSelect={handleBackToTimeSelect}/>

                <RefundInfo refundAmount={refundAmount} onlyOriginalPaymentView={onlyOriginalPaymentView} onlyCreditView={onlyCreditView}/>
                <Box as="form" sx={{
            '.chakra-form-control': {
                paddingX: 0,
                marginX: 0,
                paddingTop: 0,
            },
            '.chakra-form__label': {
                fontWeight: 'semibold',
                color: 'black',
                fontSize: 'base',
                paddingTop: 6,
                paddingBottom: 4,
            },
        }} onSubmit={handleSubmit(onSubmit)} id={REFUND_FORM_ID}>
                    {onlyCreditView && !onlyOriginalPaymentView ? (<input {...register(REFUND_FIELD, {
            shouldUnregister: true,
            value: CREDIT_VALUE,
        })} type="hidden"/>) : null}
                    {onlyOriginalPaymentView ? (<input {...register(REFUND_FIELD, {
            shouldUnregister: true,
            value: ORIGINAL_PAYMENT_METHOD_VALUE,
        })} type="hidden"/>) : null}
                    {!noOptions && (<Controller name={REFUND_FIELD} control={control} render={({ field }) => (<RadioGroup sx={{
                    '.chakra-radio__control': {
                        marginTop: 0.5,
                    },
                    '.chakra-radio__label': {
                        overflow: 'hidden',
                    },
                    '.chakra-stack div:not(:first-child)': {
                        marginTop: 3,
                    },
                }} marginBottom={4} label="Where should we send your refund?" id={REFUND_FIELD} {...field}>
                                    <BorderedRadio isSelected={field.value === CREDIT_VALUE} value={CREDIT_VALUE} radioStyles={{
                    paddingY: 3,
                    height: 'auto',
                }}>
                                        <Text marginBottom={1}>
                                            SpotHero Credit
                                        </Text>
                                        <Text color="text.secondary.light">
                                            Credit will be issued immediately
                                        </Text>
                                    </BorderedRadio>
                                    {refundCard ? (<BorderedRadio isSelected={field.value ===
                        refundCard.externalId} value={refundCard.externalId} radioStyles={{
                        paddingY: 3,
                        height: 'auto',
                    }}>
                                            <Text marginBottom={1}>
                                                {cardDisplay}
                                            </Text>
                                            <Text color="text.secondary.light">
                                                It may take 5&ndash;10 business
                                                days for the refund to appear on
                                                your account.
                                            </Text>
                                        </BorderedRadio>) : (<BorderedRadio isSelected={field.value ===
                        ORIGINAL_PAYMENT_METHOD_VALUE} value={ORIGINAL_PAYMENT_METHOD_VALUE} radioStyles={{
                        paddingY: 3,
                        height: 'auto',
                    }}>
                                            <Text marginBottom={1}>
                                                Original Payment Method
                                            </Text>
                                            <Text color="text.secondary.light">
                                                It may take 5&ndash;10 business
                                                days for the refund to appear on
                                                your account.
                                            </Text>
                                        </BorderedRadio>)}
                                </RadioGroup>)}/>)}
                </Box>
                {error && (<Alert marginTop={6} ref={errorMessageRef} title={ERROR_MAPPING[error.message].title} description={ERROR_MAPPING[error.message].description} showIcon status="error" size="md"/>)}
            </ModalBody>
            <ModalFooter justifyContent="end" gap={4} flexDirection={{ base: 'column', desktop: 'row-reverse' }}>
                <Button loadingText={'Update Reservation'} data-testid="ChangeTimesRefund-update-reservation-button" width={{ base: '100%', desktop: 'auto' }} disabled={isSubmitting} form={REFUND_FORM_ID} type="submit">
                    Update Reservation
                </Button>
                <Button width={{ base: '100%', desktop: 'auto' }} onClick={() => onCloseModal()} variant="secondary">
                    Cancel
                </Button>
            </ModalFooter>
        </>);
};
export default ChangeTimesRefund;
